<template>
  <v-dialog :value="value" persistent width="700px" max-width="90vw" @click:outside="closeModal">
    <v-card class="d-flex flex-column ruleset-shuttle-card">
      <v-card-title class="main white--text pa-2 d-flex justify-space-between">
        <span>
          Shuttle Ruleset
          <span v-if="ruleset.rule_display_name" class="text-subtitle-2 font-italic ml-2">
            {{ ruleset.rule_display_name }}
          </span>
        </span>
        <v-icon color="grey lighten-1" @click="closeModal">mdi-close</v-icon>
      </v-card-title>

      <v-toolbar flat class="flex-grow-0">
        <v-row>
          <v-col cols="6">
            <v-text-field
              label="Filter"
              prepend-inner-icon="mdi-filter-variant"
              v-model="search"
              dense
              outlined
              hide-details
              clearable
              class="mt-4"
            />
          </v-col>
        </v-row>
      </v-toolbar>

      <div class="tree-wrapper px-4 pb-2">
        <div v-if="loading" class="d-flex justify-center align-center" style="height: 200px;">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else-if="treeItems.length === 0" class="d-flex justify-center align-center grey--text" style="height: 200px;">
          No stores or store groups available
        </div>
        <v-treeview
          v-else
          v-model="selectedLeafIds"
          :items="treeItems"
          :search="search"
          :open.sync="openIds"
          item-key="id"
          item-text="name"
          item-children="children"
          selectable
          selection-type="leaf"
          open-on-click
          dense
        >
          <template #prepend="{ item, open }">
            <v-icon v-if="item.type === 'TPR_STORE_GROUP'" color="primary">
              {{ open ? 'mdi-folder-open-outline' : 'mdi-folder-outline' }}
            </v-icon>
            <v-icon v-else color="grey darken-1">mdi-store-outline</v-icon>
          </template>
          <template #label="{ item }">
            <span>{{ item.name }}</span>
            <span
              v-if="item.type === 'TPR_STORE_GROUP' && item.children && item.children.length > 0"
              class="text-caption grey--text ml-2"
            >
              ({{ item.children.length }})
            </span>
          </template>
        </v-treeview>
      </div>

      <v-toolbar flat class="flex-grow-0 ruleset-shuttle-footer">
        <span v-if="targetPartyIds.length > 0" class="text-caption grey--text text--darken-2">
          {{ targetPartyIds.length }} store{{ targetPartyIds.length === 1 ? '' : 's' }} selected
        </span>
        <v-spacer />
        <v-btn text small @click="expandAll" :disabled="isShuttling || expandableIds.length === 0">Expand all</v-btn>
        <v-btn text small @click="collapseAll" :disabled="isShuttling || openIds.length === 0">Collapse all</v-btn>
        <v-btn text @click="closeModal" :disabled="isShuttling">Cancel</v-btn>
        <v-btn
          class="ma-2 green white--text"
          :loading="isShuttling"
          :disabled="targetPartyIds.length === 0 || isShuttling"
          @click="shuttle"
        >Shuttle</v-btn>
      </v-toolbar>
    </v-card>
  </v-dialog>
</template>

<script>
import Rules from '@/axios/rules-management'

export default {
  name: 'RulesetShuttle',
  props: {
    value: { type: Boolean, default: false },
    ruleset: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      search: '',
      loading: false,
      isShuttling: false,
      treeItems: [],
      selectedLeafIds: [],
      openIds: []
    }
  },
  computed: {
    targetPartyIds() {
      return [...new Set(this.selectedLeafIds.filter(Boolean))]
    },
    expandableIds() {
      return this.treeItems
        .filter(item => item.children && item.children.length > 0)
        .map(item => item.id)
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadTargets()
        } else {
          this.resetState()
        }
      }
    }
  },
  methods: {
    resetState() {
      this.selectedLeafIds = []
      this.openIds = []
      this.search = ''
    },
    async loadTargets() {
      this.loading = true
      this.treeItems = []
      this.resetState()
      try {
        const data = await this.$store.dispatch('getUserProfileTprStoreGroups')
        const sourcePartyId = this.ruleset.party_id
        const isPriceProStore = s => {
          const flag = s?.is_price_pro_store
          return typeof flag === 'string' ? flag.toLowerCase() === 'true' : Boolean(flag)
        }
        const groups = data
          .filter(p => p.party_type === 'TPR_STORE_GROUP' && p.store_party_id !== sourcePartyId)
          .sort((a, b) => a.store_name.localeCompare(b.store_name))
        const stores = data
          .filter(p => ['STORE', 'DUMMY_STORE'].includes(p.party_type) && p.store_party_id !== sourcePartyId && isPriceProStore(p))
          .sort((a, b) => a.store_name.localeCompare(b.store_name))

        const groupNodes = groups
          .map(g => {
            const childStores = (g.children || [])
              .filter(c => c && c.store_party_id && c.store_party_id !== sourcePartyId && isPriceProStore(c))
              .sort((a, b) => a.store_name.localeCompare(b.store_name))
              .map(c => ({
                id: c.store_party_id,
                name: c.store_name,
                type: 'STORE'
              }))
            if (childStores.length === 0) return null
            return {
              id: g.store_party_id,
              name: g.store_name,
              type: 'TPR_STORE_GROUP',
              children: childStores
            }
          })
          .filter(Boolean)

        const storeNodes = stores.map(s => ({
          id: s.store_party_id,
          name: s.store_name,
          type: 'STORE'
        }))

        this.treeItems = [...groupNodes, ...storeNodes]
      } catch (err) {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: `Failed to load stores: ${err.message}`
        })
      } finally {
        this.loading = false
      }
    },
    expandAll() {
      this.openIds = [...this.expandableIds]
    },
    collapseAll() {
      this.openIds = []
    },
    async shuttle() {
      const partyIds = this.targetPartyIds
      if (partyIds.length === 0 || !this.ruleset.ruleset_id) return

      this.isShuttling = true
      try {
        const { data } = await Rules.copyRulesets(this.ruleset.ruleset_id, partyIds)
        const copied = data?.rulesets_copied ?? partyIds.length
        this.$store.dispatch('setSnackbar', {
          status: 'success',
          text: `Ruleset copied to ${copied} store${copied === 1 ? '' : 's'}.`
        })
        this.$emit('shuttled')
        this.closeModal()
      } catch (err) {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: `Failed to shuttle ruleset: ${err.response?.data?.message || err.message}`
        })
      } finally {
        this.isShuttling = false
      }
    },
    closeModal() {
      if (this.isShuttling) return
      this.$emit('update:value', false)
    }
  }
}
</script>

<style scoped>
.ruleset-shuttle-card {
  max-height: 90vh;
}
.tree-wrapper {
  flex: 1 1 auto;
  min-height: 120px;
  overflow-y: auto;
}
.ruleset-shuttle-footer {
  border-top: 1px solid #e0e0e0;
}
</style>
